import { useEffect, useMemo, useState } from "react";
import { api, type SessionAvailability, type SessionAvailabilityConflict } from "../lib/api";

export type AvailabilityPeriod = { plannedStart: string; plannedEnd: string };
type Props = {
  plannedStart: string;
  plannedEnd: string;
  roomIds?: number[];
  trainerIds?: number[];
  resourceIds?: number[];
  excludeSessionId?: number;
  /** datetime-local values need conversion to ISO before the API call. */
  toIso?: (value: string) => string;
};

type AvailabilityMap = {
  roomIds: number[];
  trainerIds: number[];
  resourceIds: number[];
  availability: SessionAvailability | null;
  loading: boolean;
  error: string | null;
};

const identity = (value: string) => value;
const emptyAvailability: SessionAvailability = { roomConflicts: [], trainerConflicts: [], resourceConflicts: [] };

function mergeConflicts(results: SessionAvailability[]): SessionAvailability {
  const unique = (key: keyof SessionAvailability) => Array.from(new Map(results.flatMap((result) => result[key]).map((conflict) => [`${conflict.id}-${conflict.sessionId}`, conflict])).values());
  return { roomConflicts: unique("roomConflicts"), trainerConflicts: unique("trainerConflicts"), resourceConflicts: unique("resourceConflicts") };
}

/** Loads all currently unavailable options, even before the user selected one. */
export function useAvailabilityMap(periods: AvailabilityPeriod[], excludeSessionId?: number, toIso: (value: string) => string = identity): AvailabilityMap {
  const [availability, setAvailability] = useState<SessionAvailability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const periodKey = periods.map((period) => `${period.plannedStart}/${period.plannedEnd}`).join(",");

  useEffect(() => {
    const validPeriods = periods.filter((period) => {
      const start = new Date(period.plannedStart);
      const end = new Date(period.plannedEnd);
      return period.plannedStart && period.plannedEnd && !Number.isNaN(start.valueOf()) && !Number.isNaN(end.valueOf()) && start < end;
    });
    if (!validPeriods.length) {
      setAvailability(null);
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;
    const timeout = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      // Deliberately omit resource IDs: the API returns every conflicting option.
      void Promise.all(validPeriods.map((period) => api.getSessionAvailability({
        plannedStart: toIso(period.plannedStart),
        plannedEnd: toIso(period.plannedEnd),
        excludeSessionId,
      }))).then((results) => {
        if (active) setAvailability(mergeConflicts(results));
      }).catch(() => {
        if (active) {
          setAvailability(null);
          setError("Verfügbarkeit konnte nicht geprüft werden.");
        }
      }).finally(() => {
        if (active) setLoading(false);
      });
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [periodKey, excludeSessionId, toIso]);

  return useMemo(() => ({
    roomIds: Array.from(new Set(availability?.roomConflicts.map((conflict) => conflict.id) ?? [])),
    trainerIds: Array.from(new Set(availability?.trainerConflicts.map((conflict) => conflict.id) ?? [])),
    resourceIds: Array.from(new Set(availability?.resourceConflicts.map((conflict) => conflict.id) ?? [])),
    availability,
    loading,
    error,
  }), [availability, loading, error]);
}

function formatRange(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  const date = s.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = (value: Date) => value.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time(s)}–${time(e)}`;
}

function ConflictList({ label, conflicts }: { label: string; conflicts: SessionAvailabilityConflict[] }) {
  if (!conflicts.length) return null;
  return <div><div className="font-medium">{label}</div><ul className="mt-1 list-inside list-disc space-y-0.5">{conflicts.map((conflict) => <li key={`${conflict.id}-${conflict.sessionId}`}><span className="font-medium">{conflict.name}</span>: „{conflict.title}“ ({formatRange(conflict.plannedStart, conflict.plannedEnd)})</li>)}</ul></div>;
}

export function SessionAvailabilityHint({ plannedStart, plannedEnd, roomIds = [], trainerIds = [], resourceIds = [], excludeSessionId, toIso = identity }: Props) {
  const { availability, loading, error } = useAvailabilityMap([{ plannedStart, plannedEnd }], excludeSessionId, toIso);
  const selectedConflicts = useMemo(() => {
    if (!availability) return emptyAvailability;
    return {
      roomConflicts: availability.roomConflicts.filter((conflict) => roomIds.includes(conflict.id)),
      trainerConflicts: availability.trainerConflicts.filter((conflict) => trainerIds.includes(conflict.id)),
      resourceConflicts: availability.resourceConflicts.filter((conflict) => resourceIds.includes(conflict.id)),
    };
  }, [availability, roomIds, trainerIds, resourceIds]);

  if (loading) return <div className="text-sm text-slate-500">Prüfe Verfügbarkeit…</div>;
  if (error) return <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{error}</div>;
  if (!availability || (!roomIds.length && !trainerIds.length && !resourceIds.length)) return null;
  const hasConflicts = selectedConflicts.roomConflicts.length || selectedConflicts.trainerConflicts.length || selectedConflicts.resourceConflicts.length;
  if (!hasConflicts) return <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">Die ausgewählten Räume, Trainer und Ressourcen sind in diesem Zeitraum verfügbar.</div>;

  return <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"><div className="font-semibold">Mögliche Belegungskonflikte</div><div className="mt-1 space-y-2"><ConflictList label="Räume" conflicts={selectedConflicts.roomConflicts} /><ConflictList label="Trainer" conflicts={selectedConflicts.trainerConflicts} /><ConflictList label="Ressourcen" conflicts={selectedConflicts.resourceConflicts} /></div><div className="mt-2 text-xs">Hinweis: Die Prüfung ist unverbindlich; beim Speichern gelten weiterhin die verbindlichen Datenbankregeln.</div></div>;
}
